import {Card} from 'platform/components';
import {Box, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

// eslint-disable-next-line no-restricted-imports -- Intended
import {mergeAll, path} from 'ramda';
import {isArray, isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {RequestItem} from '@dms/api/shared';
import {warehouseRoutes} from '@dms/routes';
import {DmsDataGrid, catchUnhandledDataGridActions, logError} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface DirectSaleMaterialReturnListProps extends RequiredTestIdProps {
  directSaleId: string;
  onAddToBasket: (selectedItems: RequestItem[]) => Promise<void>;
}

const PREDEFINED_COLUMNS = [
  'articleId',
  'warehouseId',
  'availableForReturnQuantity',
  'handlingUnit',
  'unitPriceWithoutVat',
  'unitPriceWithVat',
] as const satisfies readonly (keyof DirectSaleMaterialReturnItemRow)[];

type DirectSaleMaterialReturnItemRow = {
  id: string;
  articleId: {value: string};
  warehouseId: {value: string};
  availableForReturnQuantity: {value: number};
  handlingUnit: {value: string};
  unitPriceWithoutVat: {value: {amount: number}};
  unitPriceWithVat: {value: {amount: number}};
};

export function DirectSaleMaterialReturnList(props: DirectSaleMaterialReturnListProps) {
  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    refreshData,
    rowData,
  }) => {
    match(actionKey)
      .with('add', () => {
        const castedRowData = (
          isArray(rowData) ? rowData : [rowData]
        ) as DirectSaleMaterialReturnItemRow[];

        // Predefined columns guarantee the cells are fetched, not that they hold
        // values — skip rows whose data is not populated instead of throwing.
        const validRows = castedRowData.filter(
          (row): row is DirectSaleMaterialReturnItemRow =>
            isNotNil(row?.articleId?.value) &&
            isNotNil(row?.availableForReturnQuantity?.value) &&
            isNotNil(row?.handlingUnit?.value) &&
            isNotNil(row?.unitPriceWithoutVat?.value?.amount) &&
            isNotNil(row?.unitPriceWithVat?.value?.amount)
        );

        if (validRows.length === 0) {
          return logError('Selected items are missing required data');
        }

        const requestPayload: RequestItem[] = validRows.map((row) => ({
          originEntityId: row.id,
          articleId: row.articleId.value,
          quantity: row.availableForReturnQuantity.value,
          handlingUnit: row.handlingUnit.value,
          priceUnitWithoutVat: row.unitPriceWithoutVat.value.amount,
          priceUnitWithVat: row.unitPriceWithVat.value.amount,
        }));

        return props.onAddToBasket(requestPayload).then(() => refreshData());
      })
      .with('details', () => {
        const articleId = path(['articleId', 'value'], rowData) as string;
        const warehouseId = path(['warehouseId', 'value'], rowData) as string;

        if (isNilOrEmpty(articleId)) {
          return logError('articleId is not defined');
        }

        if (isNilOrEmpty(warehouseId)) {
          return logError('warehouseId is not defined');
        }

        return window.open(
          composePath(warehouseRoutes.articleDetailOverview, {
            params: {
              id: articleId,
              warehouseId,
            },
            isSearchParamsPreserved: false,
          }),
          '_blank'
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {directSaleId: props.directSaleId}]),
    [props.directSaleId]
  );

  return (
    <Card isFullHeight data-testid={props['data-testid']}>
      <VStack spacing={4} height="100%">
        <Box flex={1}>
          <DmsDataGrid
            gridCode="direct-sale-item-return"
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
            data-testid={suffixTestId('returnItems', props)}
          />
        </Box>
      </VStack>
    </Card>
  );
}
